import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import api, { contractsAPI, suppliersAPI, inventoryAPI, programsAPI, reportsAPI } from '@/lib/api';
import { Loader2, Download, FileText, TrendingUp, ShoppingCart, RefreshCw, Filter, Check, X as XIcon, Wallet, AlertTriangle, Link2Off } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const GROUPS_SALES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'customer', label: 'By Customer' },
  { value: 'program', label: 'By Program' },
  { value: 'item', label: 'By Item' },
  { value: 'item_type', label: 'By Item Type' },
];

const GROUPS_PURCHASE = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'supplier', label: 'By Supplier' },
  { value: 'program', label: 'By Program' },
  { value: 'item', label: 'By Item' },
  { value: 'item_type', label: 'By Item Type' },
];

const ITEM_TYPES = ['Raw Material', 'Finished Good', 'Semi Finished Good', 'Consumable', 'Tool', 'Service'];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

function formatINR(v) {
  if (!v && v !== 0) return '—';
  return 'Rs ' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function InlineRateEditor({ line, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(line.unit_price || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const price = parseFloat(val);
    if (isNaN(price) || price < 0) { toast.error('Enter a valid non-negative rate'); return; }
    setSaving(true);
    try {
      await reportsAPI.updateReceiptLineRate({
        receipt_id: line.receipt_id,
        item_id: line.item_id,
        unit_price: price,
      });
      toast.success(`Rate saved: Rs ${price}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-0.5 justify-end">
        <Input autoFocus type="number" className="h-6 text-[11px] w-20 text-right tabular-nums" value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          data-testid={`rate-input-${line.receipt_id}-${line.item_id}`} />
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={save} disabled={saving}><Check className="w-3 h-3 text-emerald-600" /></Button>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditing(false)}><XIcon className="w-3 h-3 text-slate-400" /></Button>
      </div>
    );
  }

  const missing = !line.unit_price || line.unit_price <= 0;
  return (
    <button type="button"
      onClick={() => { setVal(line.unit_price || ''); setEditing(true); }}
      className={`text-right tabular-nums w-full hover:underline decoration-dotted ${missing ? 'text-amber-600 italic' : ''}`}
      title={missing ? 'Enter rate manually (PO missing or legacy)' : 'Click to edit rate'}
      data-testid={`rate-cell-${line.receipt_id}-${line.item_id}`}>
      {missing ? '— set rate' : formatINR(line.unit_price)}
    </button>
  );
}

function monthStartIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function monthEndIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

function ProgramPnlPanel({ programs }) {
  const [mode, setMode] = useState('month');
  const [monthStr, setMonthStr] = useState(() => new Date().toISOString().slice(0, 7));
  const [startDate, setStartDate] = useState(monthStartIso());
  const [endDate, setEndDate] = useState(monthEndIso());
  const [programId, setProgramId] = useState('');
  const [includeUnassigned, setIncludeUnassigned] = useState(true);
  // Previous-period carry-forward
  const [includePrevLoss, setIncludePrevLoss] = useState(false);
  const [includePrevProfit, setIncludePrevProfit] = useState(false);
  const [prevMode, setPrevMode] = useState('auto'); // 'auto' = previous calendar month, 'custom'
  const [prevStartDate, setPrevStartDate] = useState('');
  const [prevEndDate, setPrevEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showConflicts, setShowConflicts] = useState(false);
  const [conflictCount, setConflictCount] = useState(0);
  const [expandedProg, setExpandedProg] = useState(null);

  const effectiveDates = useCallback(() => {
    if (mode === 'month') {
      const [y, m] = monthStr.split('-').map(Number);
      const s = new Date(y, m - 1, 1).toISOString().slice(0, 10);
      const e = new Date(y, m, 0).toISOString().slice(0, 10);
      return { start_date: s, end_date: e };
    }
    return { start_date: startDate, end_date: endDate };
  }, [mode, monthStr, startDate, endDate]);

  const buildParams = () => {
    const dates = effectiveDates();
    const params = { ...dates, include_unassigned: includeUnassigned };
    if (programId) params.program_id = programId;
    if (includePrevLoss) params.include_prev_loss = true;
    if (includePrevProfit) params.include_prev_profit = true;
    if ((includePrevLoss || includePrevProfit) && prevMode === 'custom' && prevStartDate && prevEndDate) {
      params.prev_start_date = prevStartDate;
      params.prev_end_date = prevEndDate;
    }
    return params;
  };

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.programPnl(buildParams());
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Report failed');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDates, programId, includeUnassigned, includePrevLoss, includePrevProfit, prevMode, prevStartDate, prevEndDate]);

  useEffect(() => {
    (async () => {
      try {
        const r = await programsAPI.listLinkageConflicts();
        setConflictCount(r.data?.total || 0);
      } catch { /* ignore */ }
    })();
  }, [showConflicts]);

  const exportFile = async (format) => {
    try {
      const params = buildParams();
      const dates = effectiveDates();
      const res = await reportsAPI.programPnlExport(params, format);
      const blob = new Blob([res.data], { type: format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf' });
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = `program_pnl_${dates.start_date}_${dates.end_date}.${format}`;
      a.click(); window.URL.revokeObjectURL(href);
      toast.success(`${format === 'xlsx' ? 'Excel' : 'PDF'} downloaded`);
    } catch { toast.error('Export failed'); }
  };

  const totals = data?.totals || {};
  const progs = data?.programs || [];

  return (
    <div className="space-y-4" data-testid="program-pnl-panel">
      {conflictCount > 0 && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[12px]">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span><b>{conflictCount}</b> item(s) are linked to more than one program. Results may double-count.</span>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setShowConflicts(true)} data-testid="open-conflicts-btn">
              <Link2Off className="w-3 h-3 mr-1" /> Fix linkage
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> P&amp;L Period</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => exportFile('xlsx')} data-testid="pnl-export-csv"><Download className="w-3 h-3 mr-1" />Excel</Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => exportFile('pdf')} data-testid="pnl-export-pdf"><Download className="w-3 h-3 mr-1" />PDF</Button>
            <Button size="sm" className="h-7 text-[11px]" onClick={run} disabled={loading} data-testid="pnl-run-btn">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Run
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[11px]">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-8 text-xs" data-testid="pnl-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Calendar month</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'month' ? (
            <div className="md:col-span-2">
              <Label className="text-[11px]">Month</Label>
              <Input type="month" className="h-8 text-xs" value={monthStr} onChange={e => setMonthStr(e.target.value)} data-testid="pnl-month" />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-[11px]">Start date</Label>
                <Input type="date" className="h-8 text-xs" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="pnl-start-date" />
              </div>
              <div>
                <Label className="text-[11px]">End date</Label>
                <Input type="date" className="h-8 text-xs" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="pnl-end-date" />
              </div>
            </>
          )}
          <div>
            <Label className="text-[11px]">Program</Label>
            <SearchableSelect
              options={[{ value: '', label: 'All programs' }, ...programs.map(p => ({ value: p.id, label: `${p.program_number || ''} · ${p.program_name}`.trim() }))]}
              value={programId} onSelect={setProgramId} placeholder="All programs" triggerClassName="h-8 text-xs"
              testId="pnl-program-filter" />
          </div>
          {!programId && (
            <div className="flex items-end">
              <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none" data-testid="pnl-include-unassigned-label">
                <input type="checkbox" className="h-3.5 w-3.5"
                  checked={includeUnassigned}
                  onChange={e => setIncludeUnassigned(e.target.checked)}
                  data-testid="pnl-include-unassigned" />
                Include Unassigned bucket
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Previous-period carry-forward */}
      <Card data-testid="pnl-carry-forward-card">
        <CardHeader className="py-2.5 px-4 border-b">
          <CardTitle className="text-[13px] flex items-center gap-1.5">
            <Filter className="w-3 h-3" /> Previous-period carry-forward
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none" data-testid="pnl-include-prev-loss-label">
              <input type="checkbox" className="h-3.5 w-3.5"
                checked={includePrevLoss}
                onChange={e => setIncludePrevLoss(e.target.checked)}
                data-testid="pnl-include-prev-loss" />
              Include previous-period <span className="text-rose-700 font-semibold">loss</span> in current cost
            </label>
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none" data-testid="pnl-include-prev-profit-label">
              <input type="checkbox" className="h-3.5 w-3.5"
                checked={includePrevProfit}
                onChange={e => setIncludePrevProfit(e.target.checked)}
                data-testid="pnl-include-prev-profit" />
              Include previous-period <span className="text-emerald-700 font-semibold">profit</span> in current sale
            </label>
            {(includePrevLoss || includePrevProfit) && (
              <>
                <span className="text-[11px] text-muted-foreground">Previous period:</span>
                <Select value={prevMode} onValueChange={setPrevMode}>
                  <SelectTrigger className="h-7 text-[11px] w-44" data-testid="pnl-prev-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Previous calendar month</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
                {prevMode === 'custom' && (
                  <>
                    <Input type="date" className="h-7 text-[11px] w-36" value={prevStartDate} onChange={e => setPrevStartDate(e.target.value)} data-testid="pnl-prev-start-date" />
                    <span className="text-[11px] text-muted-foreground">to</span>
                    <Input type="date" className="h-7 text-[11px] w-36" value={prevEndDate} onChange={e => setPrevEndDate(e.target.value)} data-testid="pnl-prev-end-date" />
                  </>
                )}
              </>
            )}
          </div>
          {(includePrevLoss || includePrevProfit) && (
            <p className="text-[10px] text-muted-foreground">
              Loss adjustment = max(0, prev cost − prev sale). Profit adjustment = max(0, prev sale − prev cost).
              If the previous period ended in the other direction, the respective adjustment is 0.
            </p>
          )}
        </CardContent>
      </Card>

      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <KpiCard label="Opening" value={totals.opening_value} muted />
            <KpiCard label="Received" value={totals.received_value} muted />
            <KpiCard label="Closing" value={totals.closing_value} muted />
            <KpiCard label="Cost" value={totals.cost_value} tone="rose" testId="kpi-cost" />
            <KpiCard label="Sale" value={totals.sale_value} tone="emerald" testId="kpi-sale" />
            <KpiCard label="Margin" value={totals.margin_value} tone={(totals.margin_value || 0) >= 0 ? 'emerald' : 'rose'} testId="kpi-margin" />
            <KpiCard label="Margin %" raw={`${(totals.margin_pct ?? 0).toFixed(2)}%`} tone={(totals.margin_pct || 0) >= 0 ? 'emerald' : 'rose'} testId="kpi-margin-pct" />
          </div>

          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-xs">Program-wise Breakdown</CardTitle>
              <CardDescription className="text-[11px]">FIFO-valued cost of goods consumed · {progs.length} program(s)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {progs.length === 0 ? (
                <p className="p-6 text-xs text-muted-foreground text-center">No programs with linked items.</p>
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-xs" data-testid="pnl-table">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Program</th>
                        <th className="text-right px-3 py-2 font-medium">Opening</th>
                        <th className="text-right px-3 py-2 font-medium">Received</th>
                        <th className="text-right px-3 py-2 font-medium">Closing</th>
                        <th className="text-right px-3 py-2 font-medium">Cost</th>
                        <th className="text-right px-3 py-2 font-medium">Sale</th>
                        <th className="text-right px-3 py-2 font-medium">Margin</th>
                        <th className="text-right px-3 py-2 font-medium">%</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {progs.map(r => (
                        <React.Fragment key={r.program_id}>
                          <tr className={`border-t hover:bg-slate-50/60 cursor-pointer ${r.is_unassigned ? 'bg-amber-50/50' : ''}`}
                              onClick={() => setExpandedProg(expandedProg === r.program_id ? null : r.program_id)}
                              data-testid={`pnl-row-${r.program_id}`}>
                            <td className="px-3 py-1.5">
                              <div className="font-medium flex items-center gap-1.5">
                                {r.is_unassigned && <AlertTriangle className="w-3 h-3 text-amber-600" />}
                                {r.program_name}
                              </div>
                              <div className="text-[10px] font-mono text-muted-foreground">{r.program_number} · {r.item_count} item(s)</div>
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{formatINR(r.opening_value)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{formatINR(r.received_value)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{formatINR(r.closing_value)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium text-rose-700">
                              {formatINR(r.cost_value)}
                              {r.prev_loss_adj > 0 && (
                                <div className="text-[9px] text-rose-600/70 font-normal" title={`Prev-period loss folded in (${formatINR(r.prev_loss_adj)})`}>
                                  incl. prev loss {formatINR(r.prev_loss_adj)}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium text-emerald-700">
                              {formatINR(r.sale_value)}
                              {r.prev_profit_adj > 0 && (
                                <div className="text-[9px] text-emerald-600/70 font-normal" title={`Prev-period profit folded in (${formatINR(r.prev_profit_adj)})`}>
                                  incl. prev profit {formatINR(r.prev_profit_adj)}
                                </div>
                              )}
                            </td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${r.margin_value >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatINR(r.margin_value)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${r.margin_pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{(r.margin_pct ?? 0).toFixed(1)}%</td>
                            <td className="px-2 text-[10px] text-muted-foreground">{expandedProg === r.program_id ? '▾' : '▸'}</td>
                          </tr>
                          {expandedProg === r.program_id && (
                            <tr className="bg-slate-50/70">
                              <td colSpan={9} className="px-4 py-2">
                                <PnlDrilldown program={r} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card><CardContent className="p-6 text-xs text-muted-foreground text-center">Click <b>Run</b> to generate the P&amp;L for the selected period.</CardContent></Card>
      )}

      <LinkageConflictsDialog open={showConflicts} onOpenChange={setShowConflicts} onResolved={() => { setShowConflicts(false); run(); }} />
    </div>
  );
}

function KpiCard({ label, value, raw, tone, testId, muted }) {
  const color = tone === 'emerald' ? 'text-emerald-700' :
                tone === 'rose' ? 'text-rose-700' :
                muted ? 'text-slate-700' : 'text-slate-900';
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`text-lg font-semibold tabular-nums mt-0.5 ${color}`}>{raw !== undefined ? raw : formatINR(value || 0)}</p>
      </CardContent>
    </Card>
  );
}

function PnlDrilldown({ program }) {
  const items = program.items || [];
  const sales = program.sale_lines || [];
  return (
    <div className="grid md:grid-cols-2 gap-3 text-[11px]">
      <div>
        <p className="font-medium mb-1">Inventory & Service (purchase items) — FIFO for inventory, direct expense for services</p>
        {items.length === 0 ? <p className="text-muted-foreground italic">No purchase items linked.</p> : (
          <table className="w-full">
            <thead><tr className="text-muted-foreground">
              <th className="text-left py-1">Item</th><th className="text-right">Open</th><th className="text-right">Received</th><th className="text-right">Close</th><th className="text-right">Cost</th>
            </tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.item_id} className="border-t">
                  <td className="py-1">
                    <div className="font-medium flex items-center gap-1">
                      {i.item_name}
                      {i.is_inventory_item === false && (
                        <span className="text-[8px] px-1 py-0 rounded border border-purple-300 text-purple-700 bg-purple-50" title="Non-inventory item — full received value is treated as cost (no FIFO).">
                          Service
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground">{i.item_unique_id}</div>
                  </td>
                  <td className="text-right tabular-nums">{formatINR(i.opening_value)}<div className="text-[9px] text-muted-foreground">{i.opening_qty} {i.uom}</div></td>
                  <td className="text-right tabular-nums">{formatINR(i.received_value)}<div className="text-[9px] text-muted-foreground">{i.received_qty}</div></td>
                  <td className="text-right tabular-nums">{formatINR(i.closing_value)}<div className="text-[9px] text-muted-foreground">{i.closing_qty}</div></td>
                  <td className="text-right tabular-nums font-medium text-rose-700">{formatINR(i.cost_consumed_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <p className="font-medium mb-1">Sales</p>
        {sales.length === 0 ? <p className="text-muted-foreground italic">No sales recorded in this period.</p> : (
          <table className="w-full">
            <thead><tr className="text-muted-foreground">
              <th className="text-left py-1">Date</th><th className="text-left">DC</th><th className="text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Value</th>
            </tr></thead>
            <tbody>
              {sales.map((s, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1">{s.date}</td>
                  <td className="font-mono text-[10px]">{s.dc_number}</td>
                  <td>{s.item_name}</td>
                  <td className="text-right tabular-nums">{s.qty}</td>
                  <td className="text-right tabular-nums font-medium text-emerald-700">{formatINR(s.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LinkageConflictsDialog({ open, onOpenChange, onResolved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await programsAPI.listLinkageConflicts();
      setItems(r.data?.conflicts || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const keep = async (itemId, programId) => {
    setResolving(itemId);
    try {
      await programsAPI.resolveLinkageConflict({ item_id: itemId, keep_program_id: programId });
      toast.success('Linkage resolved');
      await load();
      onResolved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Resolve failed'); }
    finally { setResolving(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="conflicts-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><Link2Off className="w-4 h-4 text-amber-600" /> Items linked to multiple programs</DialogTitle>
          <DialogDescription className="text-[11px]">Items can only belong to one program. Pick the program to keep; others will be unlinked.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center p-8"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-center py-6 text-xs text-muted-foreground">No conflicts — all items are linked to at most one program.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-auto">
            {items.map(c => (
              <Card key={c.item_id} data-testid={`conflict-${c.item_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <div className="text-xs font-medium">{c.item_name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{c.item_unique_id || c.item_id}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{c.program_count} programs</Badge>
                  </div>
                  <div className="space-y-1 mt-2">
                    {c.programs.map(p => (
                      <div key={p.program_id} className="flex items-center justify-between text-[11px] border rounded px-2 py-1">
                        <div>
                          <span className="font-medium">{p.program_number} · {p.program_name}</span>
                          <span className="text-muted-foreground ml-2">({p.item_type}, qty {p.quantity})</span>
                        </div>
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={resolving === c.item_id}
                                onClick={() => keep(c.item_id, p.program_id)}
                                data-testid={`keep-${c.item_id}-${p.program_id}`}>
                          {resolving === c.item_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Keep this'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function SalesSpendReportPage() {
  const [tab, setTab] = useState('sales');
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);

  // Shared filters
  const [startDate, setStartDate] = useState(daysAgoIso(180));
  const [endDate, setEndDate] = useState(todayIso());
  const [itemId, setItemId] = useState('');
  const [itemType, setItemType] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [programId, setProgramId] = useState('');
  const [groupBy, setGroupBy] = useState('month');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await contractsAPI.activeCustomers(); setCustomers(r.data || []); } catch { /* ignore */ }
      try { const r = await suppliersAPI.approvedList({ include_non_as: true }); setSuppliers(Array.isArray(r.data) ? r.data : []); } catch { /* ignore */ }
      try { const r = await inventoryAPI.getItems(); setItems((r.data?.items || r.data || []).slice(0, 500)); } catch { /* ignore */ }
      try { const r = await programsAPI.list(); setPrograms(Array.isArray(r.data) ? r.data : (r.data?.programs || [])); } catch { /* ignore */ }
    })();
  }, []);

  const runReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate, group_by: groupBy };
      if (itemId) params.item_id = itemId;
      if (itemType) params.item_type = itemType;
      if (programId) params.program_id = programId;
      if (tab === 'sales' && customerId) params.customer_id = customerId;
      if (tab === 'purchase' && supplierId) params.supplier_id = supplierId;

      const url = tab === 'sales' ? '/reports/sales-summary' : '/reports/purchase-summary';
      const res = await api.get(url, { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Report failed');
    } finally {
      setLoading(false);
    }
  }, [tab, startDate, endDate, itemId, itemType, customerId, supplierId, programId, groupBy]);

  useEffect(() => { setData(null); setGroupBy('month'); }, [tab]);
  useEffect(() => { runReport(); // eslint-disable-next-line
  }, [tab]);

  const exportFile = async (format) => {
    try {
      const params = { start_date: startDate, end_date: endDate, group_by: groupBy, format };
      if (itemId) params.item_id = itemId;
      if (itemType) params.item_type = itemType;
      if (programId) params.program_id = programId;
      if (tab === 'sales' && customerId) params.customer_id = customerId;
      if (tab === 'purchase' && supplierId) params.supplier_id = supplierId;
      const url = tab === 'sales' ? '/reports/sales-summary' : '/reports/purchase-summary';
      const res = await api.get(url, { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf' });
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = `${tab}_summary_${startDate}_${endDate}.${format}`;
      a.click(); window.URL.revokeObjectURL(href);
      toast.success(`${format === 'xlsx' ? 'Excel' : 'PDF'} downloaded`);
    } catch (err) { toast.error('Export failed'); }
  };

  const itemOptions = items.map(i => ({ value: i.id, label: `${i.item_unique_id || i.id} — ${i.item_name || i.name}` }));

  const groups = data?.groups || [];
  const lines = data?.lines || [];
  const totals = data?.totals || {};
  const maxGroupValue = Math.max(...groups.map(g => g.value || 0), 1);

  return (
    <div className="space-y-4" data-testid="sales-spend-report-page">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <FileText className="w-5 h-5" /> Sales & Spend Report
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Retrospective summary across dispatch &amp; purchase transactions. Sale prices resolve live from linked customer POs.
          <span className="ml-1 text-amber-700">Sales include only <b>invoice-reconciled</b> DCs (excludes pending reco, FOC, rejection invoices).</span>
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="sales" data-testid="tab-sales"><TrendingUp className="w-3.5 h-3.5 mr-1" />Sales</TabsTrigger>
          <TabsTrigger value="purchase" data-testid="tab-purchase"><ShoppingCart className="w-3.5 h-3.5 mr-1" />Purchases</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="tab-pnl"><Wallet className="w-3.5 h-3.5 mr-1" />Program P&amp;L</TabsTrigger>
        </TabsList>

        {tab === 'pnl' ? (
          <TabsContent value="pnl" className="space-y-4">
            <ProgramPnlPanel programs={programs} />
          </TabsContent>
        ) : (
        <TabsContent value={tab} className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Filters</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => exportFile('xlsx')} data-testid="export-csv-btn">
                  <Download className="w-3 h-3 mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => exportFile('pdf')} data-testid="export-pdf-btn">
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                <Button size="sm" className="h-7 text-[11px]" onClick={runReport} disabled={loading} data-testid="run-report-btn">
                  {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Run
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <Label className="text-[11px]">Start date</Label>
                <Input type="date" className="h-8 text-xs" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="start-date" />
              </div>
              <div>
                <Label className="text-[11px]">End date</Label>
                <Input type="date" className="h-8 text-xs" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="end-date" />
              </div>
              {tab === 'sales' ? (
                <div>
                  <Label className="text-[11px]">Customer</Label>
                  <SearchableSelect options={[{ value: '', label: 'All customers' }, ...customers.map(c => ({ value: c.id, label: c.customer_name }))]}
                    value={customerId} onSelect={setCustomerId} placeholder="All customers" triggerClassName="h-8 text-xs"
                    testId="customer-filter" />
                </div>
              ) : (
                <div>
                  <Label className="text-[11px]">Supplier</Label>
                  <SearchableSelect options={[{ value: '', label: 'All suppliers' }, ...suppliers.map(s => ({ value: s.id, label: s.supplier_name }))]}
                    value={supplierId} onSelect={setSupplierId} placeholder="All suppliers" triggerClassName="h-8 text-xs"
                    testId="supplier-filter" />
                </div>
              )}
              <div>
                <Label className="text-[11px]">Item</Label>
                <SearchableSelect options={[{ value: '', label: 'All items' }, ...itemOptions]}
                  value={itemId} onSelect={setItemId} placeholder="All items" triggerClassName="h-8 text-xs"
                  testId="item-filter" />
              </div>
              <div>
                <Label className="text-[11px]">Item type</Label>
                <Select value={itemType || 'all'} onValueChange={v => setItemType(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="item-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Program</Label>
                <SearchableSelect options={[{ value: '', label: 'All programs' }, ...programs.map(p => ({ value: p.id, label: p.program_name }))]}
                  value={programId} onSelect={setProgramId} placeholder="All programs" triggerClassName="h-8 text-xs"
                  testId="program-filter" />
              </div>
              <div>
                <Label className="text-[11px]">Group by</Label>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="h-8 text-xs" data-testid="group-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(tab === 'sales' ? GROUPS_SALES : GROUPS_PURCHASE).map(g => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Totals + Grouped chart */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total {tab === 'sales' ? 'Sales' : 'Spend'}</p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5">{formatINR(totals.value)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Qty</p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5">{(totals.qty || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Line Items</p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5">{totals.line_count || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Groups</p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5">{totals.group_count || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Grouped results */}
          <Card data-testid="grouped-results-card">
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-xs">Summary by {GROUPS_SALES.find(g => g.value === groupBy)?.label || groupBy}</CardTitle>
              <CardDescription className="text-[11px]">{groups.length} group(s)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {groups.length === 0 ? (
                <p className="p-6 text-xs text-muted-foreground text-center">No data for the selected filters.</p>
              ) : (
                <div className="divide-y">
                  {groups.map(g => (
                    <div key={g.group} className="grid grid-cols-12 gap-2 items-center px-3 py-2 hover:bg-slate-50/50 text-xs">
                      <div className="col-span-4 truncate font-medium">{g.group}</div>
                      <div className="col-span-5 h-2 rounded bg-slate-100 overflow-hidden">
                        <div className="h-full bg-emerald-400" style={{ width: `${(g.value / maxGroupValue) * 100}%` }} />
                      </div>
                      <div className="col-span-2 text-right tabular-nums">{formatINR(g.value)}</div>
                      <div className="col-span-1 text-right tabular-nums text-muted-foreground text-[11px]">{g.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line items */}
          <Card data-testid="lines-card">
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-xs">Transaction Lines</CardTitle>
              <CardDescription className="text-[11px]">Showing {lines.length} lines (first 2000)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {lines.length === 0 ? (
                <p className="p-6 text-xs text-muted-foreground text-center">No transactions.</p>
              ) : (
                <div className="max-h-[520px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Date</th>
                        <th className="text-left px-3 py-2 font-medium">{tab === 'sales' ? 'DC #' : 'GRN #'}</th>
                        <th className="text-left px-3 py-2 font-medium">{tab === 'sales' ? 'Customer' : 'Supplier'}</th>
                        <th className="text-left px-3 py-2 font-medium">Program</th>
                        <th className="text-left px-3 py-2 font-medium">Item</th>
                        <th className="text-left px-3 py-2 font-medium">Type</th>
                        <th className="text-right px-3 py-2 font-medium">Qty</th>
                        <th className="text-right px-3 py-2 font-medium">Rate</th>
                        <th className="text-right px-3 py-2 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((r, i) => (
                        <tr key={i} className="border-t hover:bg-slate-50/50">
                          <td className="px-3 py-1.5 tabular-nums">{r.date}</td>
                          <td className="px-3 py-1.5 font-mono text-[11px]">{r.dc_number || r.grn_number}</td>
                          <td className="px-3 py-1.5 truncate max-w-[160px]">{r.customer_name || r.supplier_name}</td>
                          <td className="px-3 py-1.5 truncate max-w-[140px]">
                            {r.program_name && r.program_name !== 'Unassigned'
                              ? <span className="text-[11px]">{r.program_name}</span>
                              : <span className="text-[11px] text-muted-foreground italic">Unassigned</span>}
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="font-medium">{r.item_name}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{r.item_unique_id}</div>
                          </td>
                          <td className="px-3 py-1.5">
                            {r.item_type ? <Badge variant="outline" className="text-[10px]">{r.item_type}</Badge> : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.qty?.toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {tab === 'purchase'
                              ? <InlineRateEditor line={r} onSaved={runReport} />
                              : formatINR(r.unit_price)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatINR(r.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
